import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { AU_STATES, holidaysInMonth, getSavedState, saveState } from "@/lib/auHolidays";
import {
  ChevronLeft, ChevronRight, Flame, Shield, Receipt, Wrench, Car, PawPrint, ListChecks, Sun,
} from "lucide-react";

const KIND_ICON = {
  bill: Flame, insurance: Shield, warranty: Receipt, maintenance: Wrench,
  vehicle: Car, pet: PawPrint, chore: ListChecks,
};
const COLOR = {
  clay:  { dot: "bg-hearth-clay",  chip: "bg-hearth-clay/12 text-hearth-clayDark" },
  ink:   { dot: "bg-hearth-ink",   chip: "bg-hearth-ink/10 text-hearth-ink" },
  slate: { dot: "bg-hearth-slate", chip: "bg-hearth-slate/12 text-hearth-slate" },
  coral: { dot: "bg-hearth-coral", chip: "bg-hearth-coral/15 text-hearth-coral" },
  sage:  { dot: "bg-hearth-sage",  chip: "bg-hearth-sage/15 text-hearth-sageDark" },
  amber: { dot: "bg-hearth-amber", chip: "bg-hearth-amber/20 text-[#7A5320]" },
};

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function Calendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
  const [data, setData] = useState({ events: [] });
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/calendar", { params: { year, month } });
      setData(data);
    } catch (err) {
      console.error("Failed to load calendar:", err);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  // AU public holidays overlay
  const [auState, setAuState] = useState(getSavedState());
  const holidayByDay = useMemo(() => {
    const list = holidaysInMonth(year, month, auState);
    const m = {};
    for (const h of list) m[h.day] = h.names.join(" · ");
    return m;
  }, [year, month, auState]);

  const eventsByDay = useMemo(() => {
    const m = {};
    for (const e of data.events) {
      const day = parseInt(e.date.slice(8, 10), 10);
      (m[day] = m[day] || []).push(e);
    }
    return m;
  }, [data]);

  const grid = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const startDow = (first.getDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [year, month]);

  const isToday = (d) =>
    d === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();

  const nav = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y); setSelectedDay(null);
  };

  const dayEvents = selectedDay ? (eventsByDay[selectedDay] || []) : [];

  return (
    <div>
      <PageHeader
        eyebrow="Everything dated"
        title="Calendar"
        subtitle="Bills, renewals, vaccinations, services and chores — laid out by day."
      />

      <div className="warm-card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-hearth-line">
          <div className="flex items-baseline gap-3">
            <h2 className="font-display text-3xl text-hearth-ink leading-none">{MONTHS[month - 1]}</h2>
            <span className="font-mono text-hearth-mute">{year}</span>
          </div>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-hearth-mute">
              <Sun size={13} className="text-hearth-amber" />
              <span>AU holidays</span>
              <select
                data-testid="cal-au-state"
                value={auState}
                onChange={(e) => { setAuState(e.target.value); saveState(e.target.value); }}
                className="rounded-md border border-hearth-line bg-hearth-paper text-hearth-ink px-2 py-1 text-xs font-mono"
              >
                {AU_STATES.map((s) => <option key={s} value={s}>{s === "ALL" ? "all states" : s}</option>)}
              </select>
            </label>
            <div className="flex items-center gap-1">
              <button data-testid="cal-prev" onClick={() => nav(-1)} className="p-2 rounded-md hover:bg-hearth-stone text-hearth-mute hover:text-hearth-ink"><ChevronLeft size={18} /></button>
              <button data-testid="cal-today" onClick={() => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); setSelectedDay(null); }}
                      className="pill-btn text-xs bg-hearth-stone hover:bg-hearth-line text-hearth-ink">Today</button>
              <button data-testid="cal-next" onClick={() => nav(1)} className="p-2 rounded-md hover:bg-hearth-stone text-hearth-mute hover:text-hearth-ink"><ChevronRight size={18} /></button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-hearth-line/70 bg-hearth-stone/30">
          {DOW.map((d) => (
            <div key={d} className="caption text-center py-2.5 text-hearth-mute">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 auto-rows-[112px]">
          {grid.map((d, i) => {
            const events = d ? (eventsByDay[d] || []) : [];
            const holidayName = d ? holidayByDay[d] : null;
            const dim = d == null;
            const todayCell = d && isToday(d);
            const isSelected = d === selectedDay;
            const cellKey = d ? `${year}-${month}-${d}` : `pad-${year}-${month}-${i}`;
            const cellBg = (() => {
              if (dim) return "bg-hearth-stone/30 cursor-default";
              if (todayCell) return "bg-gradient-to-br from-hearth-clay/25 via-hearth-clay/15 to-hearth-amber/10 ring-2 ring-hearth-clay shadow-[0_0_0_4px_rgba(192,97,58,0.08)]";
              if (isSelected) return "bg-hearth-clay/6 ring-1 ring-inset ring-hearth-clay/30";
              if (holidayName) return "bg-hearth-amber/12 hover:bg-hearth-amber/18";
              return "bg-hearth-paper hover:bg-hearth-stone/50";
            })();
            return (
              <button
                key={cellKey}
                data-testid={d ? `cal-day-${d}` : undefined}
                data-holiday={holidayName || undefined}
                title={holidayName || undefined}
                onClick={() => d && setSelectedDay(d)}
                disabled={dim}
                className={`relative border-r border-b border-hearth-line/60 last:border-r-0 p-2 text-left transition ${cellBg}`}
              >
                {d && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-mono ${todayCell ? "text-hearth-clayDark font-bold" : holidayName ? "text-[#7A5320] font-semibold" : "text-hearth-ink"}`}>
                        {String(d).padStart(2, "0")}
                      </span>
                      {todayCell && <span className="caption text-hearth-clayDark font-semibold inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-hearth-clay animate-pulse" />Today</span>}
                      {!todayCell && events.length > 0 && (
                        <span className="caption text-hearth-mute num">{events.length}</span>
                      )}
                    </div>
                    {holidayName && (
                      <div className="text-[10px] text-[#7A5320] italic truncate mt-0.5" data-testid={`cal-holiday-${d}`}>
                        {holidayName}
                      </div>
                    )}
                    <div className="mt-1 space-y-0.5 overflow-hidden">
                      {events.slice(0, holidayName ? 2 : 3).map((e, i) => (
                        <div key={`${e.kind}-${e.ref_id}-${e.date}-${e.subtype || e.title || i}`} className={`text-[10px] truncate rounded px-1.5 py-0.5 ${COLOR[e.color]?.chip || COLOR.clay.chip}`}>
                          {e.title}
                        </div>
                      ))}
                      {events.length > (holidayName ? 2 : 3) && (
                        <div className="text-[10px] text-hearth-mute pl-1">+{events.length - (holidayName ? 2 : 3)} more</div>
                      )}
                    </div>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <div className="warm-card p-6 mt-6 fade-up" data-testid="cal-day-detail">
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-display text-2xl text-hearth-ink">
              {MONTHS[month - 1]} <span className="num">{selectedDay}</span>
            </h3>
            <button onClick={() => setSelectedDay(null)} className="caption text-hearth-mute hover:text-hearth-ink">Close ×</button>
          </div>
          {holidayByDay[selectedDay] && (
            <div className="mb-4 rounded-xl bg-hearth-amber/12 border border-hearth-amber/30 px-4 py-3 flex items-center gap-2">
              <Sun size={16} className="text-hearth-amber" />
              <span className="text-sm text-[#7A5320] font-medium">Public holiday · {holidayByDay[selectedDay]}</span>
            </div>
          )}
          {dayEvents.length === 0 ? (
            <p className="text-hearth-mute italic">{holidayByDay[selectedDay] ? "Nothing scheduled — enjoy the holiday." : "Nothing on this day."}</p>
          ) : (
            <div className="divide-y divide-hearth-line/70">
              {dayEvents.map((e, i) => {
                const Icon = KIND_ICON[e.kind] || Flame;
                const key = `${e.kind}-${e.ref_id}-${e.date}-${e.subtype || e.title || i}`;
                return (
                  <Link key={key} to={e.link} data-testid={`cal-event-${e.ref_id}`} className="flex items-center gap-3 py-3 group">
                    <span className={`w-1.5 h-1.5 rounded-full ${COLOR[e.color]?.dot || COLOR.clay.dot}`} />
                    <Icon size={15} className="text-hearth-mute" />
                    <span className="text-hearth-ink flex-1 truncate group-hover:text-hearth-clay transition">{e.title}</span>
                    <span className="caption">{e.kind}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
